import { motion } from "framer-motion";
import { Apple, Play, Shield, Zap, ArrowUpRight } from "lucide-react";
import { DOWNLOAD_LINK } from "@/lib/constants";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-28 sm:pt-36 pb-20 md:pb-28 overflow-hidden grain bg-white"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-spotlight pointer-events-none" />
      <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#3B6FBF]/30 bg-[#3B6FBF]/10 text-xs text-[#2f5ca3] mb-6 font-semibold" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Now live across Nigeria
            </div>

            <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl text-slate-900" data-testid="hero-title">
              Crypto to Naira,
              <br />
              <span className="text-[#3B6FBF]">easy.</span>{" "}
              <span className="text-gradient">seamless.</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg" data-testid="hero-subtitle">
              Stop chasing P2P buyers. Convert USDT and BTC directly to Naira in under 60 seconds — at the best rates in the market, with rate-match guaranteed.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-cta-row">
              <a
                href={DOWNLOAD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-slate-900 text-white rounded-2xl px-5 py-3.5 hover:bg-slate-800 transition-all"
                data-testid="hero-appstore-btn"
              >
                <Apple className="w-7 h-7" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wide opacity-70">Download on the</div>
                  <div className="font-display font-bold text-base">App Store</div>
                </div>
              </a>
              <a
                href={DOWNLOAD_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#3B6FBF] hover:bg-[#2f5ca3] text-white rounded-2xl px-5 py-3.5 transition-all"
                data-testid="hero-playstore-btn"
              >
                <Play className="w-7 h-7 fill-white" />
                <div className="text-left leading-tight">
                  <div className="text-[10px] uppercase tracking-wide opacity-80">Get it on</div>
                  <div className="font-display font-bold text-base">Google Play</div>
                </div>
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 text-xs sm:text-sm text-slate-600">
              <div className="flex items-center gap-2" data-testid="hero-stat-instant">
                <Zap className="w-4 h-4 text-[#3B6FBF]" />
                <span>&lt; 60s settlement</span>
              </div>
              <div className="flex items-center gap-2" data-testid="hero-stat-secure">
                <Shield className="w-4 h-4 text-[#3B6FBF]" />
                <span>Bank-grade security</span>
              </div>
              <div className="items-center gap-2 hidden sm:flex" data-testid="hero-stat-users">
                <span className="font-mono-data text-slate-900 font-bold">10,000+</span>
                <span>traders</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex justify-center lg:justify-end"
            data-testid="hero-mockup-wrapper"
          >
            <div className="absolute inset-0 -m-8 bg-gradient-to-br from-[#3B6FBF]/15 via-transparent to-transparent rounded-[3rem] blur-3xl pointer-events-none" />
            <img
              src="/pamapay-mockup.jpg"
              alt="PamaPay app screens"
              className="relative w-full max-w-[560px] h-auto rounded-3xl shadow-[0_30px_80px_-20px_rgba(59,111,191,0.45)]"
              data-testid="hero-mockup-image"
            />
            <a
              href={DOWNLOAD_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 backdrop-blur-md bg-white/90 border border-slate-200 rounded-full px-4 py-2 text-xs font-semibold text-slate-900 hover:bg-white transition-all shadow-md"
              data-testid="hero-floating-cta"
            >
              Try it now <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
